import React from 'react';
import { MapPin, Navigation } from 'lucide-react';
import ListingAttributesFilterForm from './ListingAttributesFilterForm';
import { PawFilterRow } from './PawFilterRow';
import { FilterSectionBoneIcon } from './FilterSectionBoneIcon';
import { FilterDashedSeparator } from './FilterDashedSeparator';

const pawFill = (selected, blueAccent) => {
  if (!selected) return '#bebebe';
  return blueAccent ? '#3b82f6' : 'black';
};

const PawIcon = ({ selected = false, blueAccent = false }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M8 4C8.55228 4 9 3.55228 9 3C9 2.44772 8.55228 2 8 2C7.44772 2 7 2.44772 7 3C7 3.55228 7.44772 4 8 4Z" fill={pawFill(selected, blueAccent)} />
    <path d="M5 5C5.55228 5 6 4.55228 6 4C6 3.44772 5.55228 3 5 3C4.44772 3 4 3.44772 4 4C4 4.55228 4.44772 5 5 5Z" fill={pawFill(selected, blueAccent)} />
    <path d="M11 5C11.5523 5 12 4.55228 12 4C12 3.44772 11.5523 3 11 3C10.4477 3 10 3.44772 10 4C10 4.55228 10.4477 5 11 5Z" fill={pawFill(selected, blueAccent)} />
    <path d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z" fill={pawFill(selected, blueAccent)} />
    <path d="M3 9C3.55228 9 4 8.55228 4 8C4 7.44772 3.55228 7 3 7C2.44772 7 2 7.44772 2 8C2 8.55228 2.44772 9 3 9Z" fill={pawFill(selected, blueAccent)} />
    <path d="M13 9C13.5523 9 14 8.55228 14 8C14 7.44772 13.5523 7 13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9Z" fill={pawFill(selected, blueAccent)} />
  </svg>
);

/** Range slider handle — `public/assets/pawthumb.svg` */
const SliderPawThumb = () => (
  <img
    src="/assets/pawthumb.svg"
    alt=""
    aria-hidden
    draggable={false}
    className="h-[26px] w-auto max-h-full max-w-[22px] object-contain select-none"
  />
);

const WhitePawIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M8 4C8.55228 4 9 3.55228 9 3C9 2.44772 8.55228 2 8 2C7.44772 2 7 2.44772 7 3C7 3.55228 7.44772 4 8 4Z" fill="white"/>
    <path d="M5 5C5.55228 5 6 4.55228 6 4C6 3.44772 5.55228 3 5 3C4.44772 3 4 3.44772 4 4C4 4.55228 4.44772 5 5 5Z" fill="white"/>
    <path d="M11 5C11.5523 5 12 4.55228 12 4C12 3.44772 11.5523 3 11 3C10.4477 3 10 3.44772 10 4C10 4.55228 10.4477 5 11 5Z" fill="white"/>
    <path d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z" fill="white"/>
    <path d="M3 9C3.55228 9 4 8.55228 4 8C4 7.44772 3.55228 7 3 7C2.44772 7 2 7.44772 2 8C2 8.55228 2.44772 9 3 9Z" fill="white"/>
    <path d="M13 9C13.5523 9 14 8.55228 14 8C14 7.44772 13.5523 7 13 7C12.4477 7 12 7.44772 12 8C12 8.55228 12.4477 9 13 9Z" fill="white"/>
  </svg>
);

/**
 * Shared filter fields for search (mobile sheet + desktop sidebar).
 * Controlled: `value` = full advancedFilters object, `onChange` receives next full object.
 */
const SearchFiltersForm = ({
  value,
  onChange,
  cityOptions = [],
  variant = 'modal',
  className = '',
  onOpenMoreFilters,
  hideNearMe = false
}) => {
  const primary = value.primary || {};
  const secondary = value.secondary || {};

  const merge = (patch) => onChange({ ...value, ...patch });
  const updatePrimary = (key, v) =>
    merge({ primary: { ...primary, [key]: v } });

  const toggleMultiSelect = (key, opt) => {
    const current = primary[key] || [];
    const next = current.includes(opt) ? current.filter((x) => x !== opt) : [...current, opt];
    updatePrimary(key, next);
  };

  const budgetValue = primary.budget || 0;
  const budgetPercentage = (budgetValue / 20) * 100;

  const requestNearMe = () => {
    if (!navigator.geolocation) {
      alert('Location is not supported in this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updatePrimary('nearMe', {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          radiusKm: primary.nearMe?.radiusKm ?? 50
        });
      },
      () => alert('Could not get your location. Check permissions.')
    );
  };

  if (variant === 'searchResults') {
    return (
      <div className={`p-[16px] ${className}`}>
        <div>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-[14px] font-regular text-black">Property type</h3>
            <FilterSectionBoneIcon size={20} />
          </div>
          <div className="flex flex-col gap-[8px]">
            {['Lake', 'Beach', 'Riverside', 'Pool-only'].map((option) => {
              const isSelected = (primary.waterbody || []).includes(option);
              return (
                <PawFilterRow
                  key={option}
                  selected={isSelected}
                  blueAccent
                  aria-pressed={isSelected}
                  aria-label={option}
                  onClick={() => toggleMultiSelect('waterbody', option)}
                >
                  <span
                    className={`text-[13px] font-regularleading-snug ${
                      isSelected ? 'text-[#3b82f6]' : 'text-black'
                    }`}
                  >
                    {option}
                  </span>
                </PawFilterRow>
              );
            })}
          </div>
        </div>

        <FilterDashedSeparator className="my-[20px]" />

        <div>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-[14px] font-regular text-black">Pet Charges</h3>
            <FilterSectionBoneIcon size={20} />
          </div>
          <div className="flex flex-col gap-[8px]">
            {['Free', 'Paid'].map((option) => {
              const isSelected = primary.petCharges === option;
              return (
                <PawFilterRow
                  key={option}
                  selected={isSelected}
                  blueAccent
                  aria-pressed={isSelected}
                  aria-label={option}
                  onClick={() => updatePrimary('petCharges', isSelected ? null : option)}
                >
                  <span
                    className={`text-[13px] font-regularleading-snug ${
                      isSelected ? 'text-[#3b82f6]' : 'text-black'
                    }`}
                  >
                    {option}
                  </span>
                </PawFilterRow>
              );
            })}
          </div>
          {primary.petCharges === 'Paid' && (
            <div className="mt-4">
              <p className="mb-2 text-[12px] text-[#6b7280]">Max pet charge (₹K)</p>
              <div className="relative py-1">
                <div className="relative h-2 w-full rounded-full bg-[#e8e8e8]">
                  <div
                    className="absolute h-full rounded-l-full bg-[#3b82f6]"
                    style={{ width: `${((primary.petChargesAmount || 0) / 10) * 100}%` }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={primary.petChargesAmount || 0}
                    onChange={(e) => updatePrimary('petChargesAmount', parseInt(e.target.value, 10))}
                    className="absolute top-0 z-10 h-2 w-full cursor-pointer opacity-0"
                  />
                  <div
                    className="pointer-events-none absolute top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[#3b82f6] bg-white shadow"
                    style={{ left: `calc(${((primary.petChargesAmount || 0) / 10) * 100}% - 12px)` }}
                  >
                    <SliderPawThumb />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <FilterDashedSeparator className="my-[20px]" />

        <div>
          <div className="mb-2 flex items-center gap-2">
            <h3 className="text-[14px] font-regular text-black">Budget</h3>
            <FilterSectionBoneIcon size={20} />
          </div>
          <div className="space-y-3">
            <p className="text-[13px] font-medium text-[#3b82f6]">Budget : ₹{budgetValue}K</p>
            <div className="relative py-1">
              <div className="relative h-2 w-full bg-[#e8e8e8]">
                <div className="absolute h-full bg-[#000000]" style={{ width: `${budgetPercentage}%` }} />
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={budgetValue}
                  onChange={(e) => updatePrimary('budget', parseInt(e.target.value, 10))}
                  className="absolute top-0 z-10 h-2 w-full cursor-pointer opacity-0"
                />
                <div
                  className="pointer-events-none absolute top-1/2 z-20 flex -translate-y-1/2"
                  style={{ left: `calc(${budgetPercentage}% - 6px)` }}
                >
                  <SliderPawThumb />
                </div>
              </div>
              <div className="mt-2 flex justify-between text-[12px] text-[#6b7280]">
                <span>₹0</span>
                <span>₹20K</span>
              </div>
            </div>
          </div>
        </div>

        <FilterDashedSeparator className="my-[20px]" />

        <div>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-[14px] font-regular text-black">Lawn type</h3>
            <FilterSectionBoneIcon size={20} />
          </div>
          <div className="flex flex-col gap-[8px]">
            {['Trek', 'Trail', 'Lawn', 'Open Area'].map((option) => {
              const isSelected = (primary.trekTrail || []).includes(option);
              return (
                <PawFilterRow
                  key={option}
                  selected={isSelected}
                  blueAccent
                  aria-pressed={isSelected}
                  aria-label={option}
                  onClick={() => toggleMultiSelect('trekTrail', option)}
                >
                  <span
                    className={`text-[13px] font-regularleading-snug ${
                      isSelected ? 'text-[#3b82f6]' : 'text-black'
                    }`}
                  >
                    {option}
                  </span>
                </PawFilterRow>
              );
            })}
          </div>
        </div>

        <FilterDashedSeparator className="my-[20px]" />

        <div>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-[14px] font-regular text-black">Location</h3>
            <FilterSectionBoneIcon size={20} />
          </div>
          <div>
            <div className="space-y-2 py-1">
              <p className="text-[12px] text-[#6b7280]">City or region</p>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ca3af]" />
                <select
                  value={primary.filterCity || ''}
                  onChange={(e) => updatePrimary('filterCity', e.target.value)}
                  className="w-full appearance-none rounded-lg border border-[#ebebeb] bg-white py-2.5 pl-10 pr-3 text-[14px] text-black"
                >
                  <option value="">Any</option>
                  {cityOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {!hideNearMe ? (
              <div className="mt-3">
                <PawFilterRow
                  selected={Boolean(primary.nearMe)}
                  blueAccent
                  aria-pressed={Boolean(primary.nearMe)}
                  aria-label="Near me"
                  onClick={() => {
                    if (primary.nearMe) updatePrimary('nearMe', null);
                    else requestNearMe();
                  }}
                >
                  <span
                    className={`text-[13px] font-regularleading-snug ${
                      primary.nearMe ? 'text-[#3b82f6]' : 'text-black'
                    }`}
                  >
                    Near me
                  </span>
                </PawFilterRow>
              </div>
            ) : null}
          </div>
        </div>

        <FilterDashedSeparator className="my-[20px]" />

        <div>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-[14px] font-regular text-black">Dogs in pool</h3>
            <FilterSectionBoneIcon size={20} />
          </div>
          <div className="flex flex-col gap-[8px]">
            {['Yes', 'No', 'Separate Pet Pool'].map((option) => {
              const isSelected = primary.dogsInPool === option;
              return (
                <PawFilterRow
                  key={option}
                  selected={isSelected}
                  blueAccent
                  aria-pressed={isSelected}
                  aria-label={option}
                  onClick={() => updatePrimary('dogsInPool', isSelected ? null : option)}
                >
                  <span
                    className={`text-[13px] font-regularleading-snug ${
                      isSelected ? 'text-[#3b82f6]' : 'text-black'
                    }`}
                  >
                    {option}
                  </span>
                </PawFilterRow>
              );
            })}
          </div>
        </div>

        <FilterDashedSeparator className="my-[20px]" />

        <div>
          <div className="mb-3 flex items-center gap-2">
            <h3 className="text-[14px] font-regular text-black">Ratings</h3>
            <FilterSectionBoneIcon size={20} />
          </div>
          <div className="flex flex-col gap-[8px]">
            {['4.0+', '4.5+', '4.8+'].map((option) => {
              const rating = parseFloat(option);
              const isSelected = primary.minRating === rating;
              return (
                <PawFilterRow
                  key={option}
                  selected={isSelected}
                  blueAccent
                  aria-pressed={isSelected}
                  aria-label={`Minimum rating ${option}`}
                  onClick={() => updatePrimary('minRating', isSelected ? 0 : rating)}
                >
                  <span
                    className={`text-[13px] font-regularleading-snug ${
                      isSelected ? 'text-[#3b82f6]' : 'text-black'
                    }`}
                  >
                    Min {option}
                  </span>
                </PawFilterRow>
              );
            })}
          </div>
        </div>

        <FilterDashedSeparator className="my-[20px]" />

        <div>
          <ListingAttributesFilterForm
            embedded
            useModalChipStyle
            headingBoneClassName="shrink-0 text-black"
            secondary={secondary}
            onChange={(next) => merge({ secondary: next })}
          />
        </div>

        {onOpenMoreFilters ? (
          <div className="pt-4 md:hidden">
            <button
              type="button"
              onClick={onOpenMoreFilters}
              className="text-[14px] font-medium text-[#3b82f6] underline decoration-[#93c5fd] underline-offset-2 hover:text-[#2563eb]"
            >
              More filters (pool, ratings, amenities…)
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  const pad = variant === 'sidebar' ? 'pr-1' : '';

  return (
    <div className={`space-y-6 ${pad} ${className}`}>
      {/* Budget */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[16px] font-semibold text-black">💰 Budget / Price Range (₹ per night)</span>
          <FilterSectionBoneIcon size={20} />
        </div>
        <p className="text-[13px] text-[#6b7280] mb-2">Up to ₹{budgetValue}K / night (min room price)</p>
        <div className="relative py-2">
          <div className="relative w-full h-[8px] bg-[#e3e3e3] rounded-lg">
            <div className="absolute h-full bg-black rounded-l-lg" style={{ width: `${budgetPercentage}%` }} />
            <input
              type="range"
              min="0"
              max="20"
              value={budgetValue}
              onChange={(e) => updatePrimary('budget', parseInt(e.target.value, 10))}
              className="absolute w-full h-[8px] opacity-0 cursor-pointer z-10 top-0"
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 w-[24px] h-[24px] bg-black rounded-full flex items-center justify-center pointer-events-none z-20"
              style={{ left: `calc(${budgetPercentage}% - 12px)` }}
            >
              <WhitePawIcon />
            </div>
          </div>
          <div className="flex justify-between mt-2 text-[12px] text-black">
            <span>₹0</span>
            <span>₹20K</span>
          </div>
        </div>
      </div>

      {/* Waterbody */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[16px] font-semibold text-black">🏞️ Lake / Waterbody Nearby</span>
          <FilterSectionBoneIcon size={20} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {['Lake', 'Beach', 'Riverside', 'Pool-only'].map((option) => {
            const isSelected = (primary.waterbody || []).includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggleMultiSelect('waterbody', option)}
                className={`px-3 py-2 rounded-full border text-left transition-colors ${
                  isSelected ? 'bg-white border-black' : 'bg-[#f8f8f8] border-[#e3e3e3]'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <PawIcon selected={isSelected} />
                  <span className={`text-[13px] font-regular${isSelected ? 'text-black' : 'text-[#bebebe]'}`}>
                    {option}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Pet Charges */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[16px] font-semibold text-black">🐾 Pet Charges</span>
          <FilterSectionBoneIcon size={20} />
        </div>
        <div className="flex flex-wrap gap-2">
          {['Free', 'Paid'].map((option) => {
            const isSelected = primary.petCharges === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => updatePrimary('petCharges', isSelected ? null : option)}
                className={`px-4 py-2 rounded-full border transition-colors ${
                  isSelected ? 'bg-white border-black' : 'bg-[#f8f8f8] border-[#e3e3e3]'
                }`}
              >
                <div className="flex items-center gap-1">
                  <PawIcon selected={isSelected} />
                  <span className={`text-[14px] font-medium ${isSelected ? 'text-black' : 'text-[#bebebe]'}`}>
                    {option}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {primary.petCharges === 'Paid' && (
          <div className="mt-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[14px] text-black">💸 Pet charges amount (max ₹{primary.petChargesAmount || 0}K)</span>
            </div>
            <div className="relative py-1">
              <div className="relative w-full h-[8px] bg-[#e3e3e3] rounded-lg">
                <div
                  className="absolute h-full bg-black rounded-l-lg"
                  style={{ width: `${((primary.petChargesAmount || 0) / 10) * 100}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={primary.petChargesAmount || 0}
                  onChange={(e) => updatePrimary('petChargesAmount', parseInt(e.target.value, 10))}
                  className="absolute w-full h-[8px] opacity-0 cursor-pointer z-10 top-0"
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-[22px] h-[22px] bg-black rounded-full flex items-center justify-center pointer-events-none z-20"
                  style={{ left: `calc(${((primary.petChargesAmount || 0) / 10) * 100}% - 11px)` }}
                >
                  <WhitePawIcon />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Dogs in pool */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[16px] font-semibold text-black">🏊‍♂️ Dogs Allowed in Pool</span>
          <FilterSectionBoneIcon size={20} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {['Yes', 'No', 'Separate Pet Pool'].map((option) => {
            const isSelected = primary.dogsInPool === option;
            return (
              <button
                key={option}
                type="button"
                onClick={() => updatePrimary('dogsInPool', isSelected ? null : option)}
                className={`px-3 py-2 rounded-full border transition-colors ${
                  isSelected ? 'bg-white border-black' : 'bg-[#f8f8f8] border-[#e3e3e3]'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <PawIcon selected={isSelected} />
                  <span className={`text-[13px] font-regular${isSelected ? 'text-black' : 'text-[#bebebe]'}`}>
                    {option}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Trek / lawn */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[16px] font-semibold text-black">🏕️ Trek / Trail / Lawn / Open Area</span>
          <FilterSectionBoneIcon size={20} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {['Trek', 'Trail', 'Lawn', 'Open Area'].map((option) => {
            const isSelected = (primary.trekTrail || []).includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggleMultiSelect('trekTrail', option)}
                className={`px-3 py-2 rounded-full border transition-colors ${
                  isSelected ? 'bg-white border-black' : 'bg-[#f8f8f8] border-[#e3e3e3]'
                }`}
              >
                <div className="flex items-center justify-center gap-1">
                  <PawIcon selected={isSelected} />
                  <span className={`text-[13px] font-regular${isSelected ? 'text-black' : 'text-[#bebebe]'}`}>
                    {option}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Ratings */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[16px] font-semibold text-black">⭐ Ratings / Reviews</span>
          <FilterSectionBoneIcon size={20} />
        </div>
        <div className="flex flex-wrap gap-2">
          {['4.0+', '4.5+', '4.8+'].map((option) => {
            const rating = parseFloat(option);
            const isSelected = primary.minRating === rating;
            return (
              <button
                key={option}
                type="button"
                onClick={() => updatePrimary('minRating', isSelected ? 0 : rating)}
                className={`px-4 py-2 rounded-full border transition-colors ${
                  isSelected ? 'bg-white border-black' : 'bg-[#f8f8f8] border-[#e3e3e3]'
                }`}
              >
                <div className="flex items-center gap-1">
                  <PawIcon selected={isSelected} />
                  <span className={`text-[14px] font-medium ${isSelected ? 'text-black' : 'text-[#bebebe]'}`}>
                    Min {option}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <FilterDashedSeparator className="my-[20px]" />

      {/* Location */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[16px] font-semibold text-black">📍 Location</span>
          <FilterSectionBoneIcon size={20} />
        </div>
        <p className="text-[12px] text-[#6b7280] mb-3">
          {hideNearMe
            ? 'Narrow by city (stays with listing city only).'
            : 'Narrow by city or use your current position (stays with map coordinates only).'}
        </p>
        <label className="block text-[13px] font-regulartext-black mb-1">City / region</label>
        <div className={`relative ${hideNearMe ? 'mb-0' : 'mb-3'}`}>
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9ca3af]" />
          <select
            value={primary.filterCity || ''}
            onChange={(e) => updatePrimary('filterCity', e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-[#e3e3e3] bg-white text-[14px] text-black appearance-none"
          >
            <option value="">Any (use search bar location)</option>
            {cityOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        {!hideNearMe ? (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <button
                type="button"
                onClick={requestNearMe}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black text-white text-[14px] font-medium hover:bg-gray-800"
              >
                <Navigation className="w-4 h-4" />
                Near me
              </button>
              {primary.nearMe && (
                <button
                  type="button"
                  onClick={() => updatePrimary('nearMe', null)}
                  className="text-[14px] text-[#6b7280] underline"
                >
                  Clear location filter
                </button>
              )}
            </div>
            {primary.nearMe && (
              <div className="mt-3">
                <label className="text-[13px] text-black">
                  Radius: {primary.nearMe.radiusKm ?? 50} km
                </label>
                <input
                  type="range"
                  min="10"
                  max="150"
                  step="5"
                  value={primary.nearMe.radiusKm ?? 50}
                  onChange={(e) =>
                    updatePrimary('nearMe', {
                      ...primary.nearMe,
                      radiusKm: parseInt(e.target.value, 10)
                    })
                  }
                  className="w-full mt-1"
                />
              </div>
            )}
          </>
        ) : null}
      </div>

      <ListingAttributesFilterForm
        secondary={secondary}
        onChange={(next) => merge({ secondary: next })}
      />
    </div>
  );
};

export default SearchFiltersForm;
